import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  BatteryCharging,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Download,
  Factory,
  List,
  Loader2,
  RefreshCw,
  Zap,
} from 'lucide-react';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { logUsageEvent } from '../../../src/lib/logger';
import { RateioAjusteStatus, RateioAjusteUc, RateioCredito, RateioUnidadeGeradora } from '../../../types';
import type { FaturaConsumoResumo } from './consumoHistorico';
import { gerarUltimosMeses, limparUc } from './mesReferenciaRateio';
import AvisoReinclusaoRateio from './AvisoReinclusaoRateio';
import RegistrarAjusteModal from './RegistrarAjusteModal';
import RegistrarGeracaoModal from './RegistrarGeracaoModal';
import SaldoUcDetalheModal from './SaldoUcDetalheModal';
import UsinaUcsModal from './UsinaUcsModal';
import {
  ajusteAberto,
  conferirAjuste,
  formatarKwh,
  formatarPercentRateio,
  montarLinhasPainel,
  resumirPorGeradora,
  STATUS_AJUSTE,
  type FiltrosSaldos,
  type ItemRateioPainel,
  type LinhaSaldoUc,
  type UcPainel,
  type UgPainel,
} from './saldosUcs';
import {
  atualizarStatusAjuste,
  carregarAjustes,
  carregarGeradoras,
  carregarHistorico,
  carregarItensRateio,
  carregarUcs,
  listarRateios,
} from './saldosUcsDados';
import { exportarSaldosUcs } from './saldosUcsExportacao';
import { destravarUc } from './rateioTravamentoActions';

type Dados = {
  itens: ItemRateioPainel[];
  itensAnteriores: ItemRateioPainel[] | null;
  historico: Map<number, FaturaConsumoResumo[]>;
};

/** O que o modal de UCs abre: uma usina (ou todas, com `null`) e, se for o caso, filtros já aplicados. */
type AberturaModal = { usina: string | null; filtros?: Partial<FiltrosSaldos> };

/** UCs fora do rateio com saldo acabando, a de menos saldo primeiro (#6190). */
const FILTROS_REINCLUIR: Partial<FiltrosSaldos> = { status: 'todos', divergencia: 'reincluir_no_rateio', ordenacao: 'cobertura_asc' };

const SaldosUcsView: React.FC = () => {
  const { user, profile } = useAuth();
  const { addToast } = useToast();
  // `?filtro=reincluir` é o destino do alerta da Vick: abre direto a lista de quem precisa voltar ao rateio.
  const [searchParams, setSearchParams] = useSearchParams();
  const filtroDaUrl = useRef(searchParams.get('filtro'));

  const [rateios, setRateios] = useState<RateioCredito[]>([]);
  const [rateioId, setRateioId] = useState<string>('');
  const [ucs, setUcs] = useState<UcPainel[] | null>(null);
  const [ugs, setUgs] = useState<UgPainel[]>([]);
  const [cadastroUgs, setCadastroUgs] = useState<RateioUnidadeGeradora[]>([]);
  const [ajustes, setAjustes] = useState<RateioAjusteUc[]>([]);
  const [dados, setDados] = useState<Dados | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [modalUcs, setModalUcs] = useState<AberturaModal | null>(null);
  const [detalhe, setDetalhe] = useState<LinhaSaldoUc | null>(null);
  const [ajustando, setAjustando] = useState<LinhaSaldoUc | null>(null);
  const [registrarGeracao, setRegistrarGeracao] = useState<RateioUnidadeGeradora | null>(null);
  const [atualizandoAjusteId, setAtualizandoAjusteId] = useState<string | null>(null);
  const [aplicandoLote, setAplicandoLote] = useState(false);

  const rateioAtual = rateios.find(r => r.id === rateioId) || null;
  const mesRateio = rateioAtual?.mes_referencia || '';

  // Base que não depende do mês: lista de rateios, cadastro, usinas e ajustes.
  const carregarBase = useCallback(async () => {
    const [listaRateios, listaUcs, geradoras, listaAjustes] = await Promise.all([
      listarRateios(),
      carregarUcs(),
      carregarGeradoras(),
      carregarAjustes(),
    ]);
    setRateios(listaRateios);
    setUcs(listaUcs);
    setUgs(geradoras.ugs);
    setCadastroUgs(geradoras.cadastro);
    setAjustes(listaAjustes);
    setRateioId(atual => atual || listaRateios[0]?.id || '');
    return { listaRateios, listaUcs };
  }, []);

  const carregarRateio = useCallback(async (id: string, listaRateios: RateioCredito[], listaUcs: UcPainel[]) => {
    const idx = listaRateios.findIndex(r => r.id === id);
    const anterior = idx >= 0 ? listaRateios[idx + 1] : undefined;
    const [itens, itensAnteriores] = await Promise.all([
      carregarItensRateio(id),
      anterior ? carregarItensRateio(anterior.id) : Promise.resolve(null),
    ]);
    // Histórico de faturas só das UCs deste rateio (pelos dois números).
    const porChave = new Map<string, UcPainel>();
    for (const uc of listaUcs) {
      for (const c of [limparUc(uc.numero_uc), limparUc(uc.outro_numero_uc)]) if (c) porChave.set(c, uc);
    }
    const doRateio = new Map<number, UcPainel>();
    for (const item of itens) {
      const uc = porChave.get(limparUc(item.uc)) || porChave.get(limparUc(item.outro_numero_uc));
      if (uc) doRateio.set(uc.id, uc);
    }
    const historico = await carregarHistorico(Array.from(doRateio.values()));
    setDados({ itens, itensAnteriores, historico });
  }, []);

  const recarregarTudo = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const { listaRateios, listaUcs } = await carregarBase();
      const id = rateioId || listaRateios[0]?.id;
      if (id) await carregarRateio(id, listaRateios, listaUcs);
      else setDados({ itens: [], itensAnteriores: null, historico: new Map() });
    } catch (e: any) {
      console.error('Erro ao carregar painel de saldos', e);
      setErro(e?.message || 'Erro ao carregar os dados.');
    } finally {
      setCarregando(false);
    }
  }, [carregarBase, carregarRateio, rateioId]);

  useEffect(() => {
    recarregarTudo();
    // Só na abertura; a troca de mês usa `trocarRateio`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trocarRateio = async (id: string) => {
    if (!ucs) return;
    setRateioId(id);
    setCarregando(true);
    setErro(null);
    try {
      await carregarRateio(id, rateios, ucs);
    } catch (e: any) {
      console.error('Erro ao carregar rateio', e);
      setErro(e?.message || 'Erro ao carregar o rateio.');
    } finally {
      setCarregando(false);
    }
  };

  const linhas = useMemo<LinhaSaldoUc[]>(() => {
    if (!dados || !ucs || !mesRateio) return [];
    return montarLinhasPainel({
      itens: dados.itens,
      itensAnteriores: dados.itensAnteriores,
      ucs,
      historicoPorUc: dados.historico,
      ugs,
      mesRateio,
    });
  }, [dados, ucs, ugs, mesRateio]);

  const ajustesDaLinha = useCallback(
    (l: Pick<LinhaSaldoUc, 'ucId' | 'ucNumero' | 'ucNovo'>) =>
      ajustes.filter(a =>
        (l.ucId != null && a.unidade_consumidora_id === l.ucId) ||
        a.uc_numero === l.ucNumero ||
        (!!l.ucNovo && a.uc_numero === l.ucNovo)
      ),
    [ajustes]
  );

  const resumoUsinas = useMemo(() => resumirPorGeradora(linhas, ugs), [linhas, ugs]);

  const reinclusao = useMemo(() => {
    const lista = linhas.filter(l => l.divergencias.includes('reincluir_no_rateio'));
    return {
      total: lista.length,
      pedidas: lista.filter(l => ajustesDaLinha(l).some(a => a.tipo === 'incluir' && ajusteAberto(a))).length,
      pausadas: lista.filter(l => l.statusCadastro === 'Pausada').length,
    };
  }, [linhas, ajustesDaLinha]);

  useEffect(() => {
    if (filtroDaUrl.current !== 'reincluir' || linhas.length === 0) return;
    filtroDaUrl.current = null;
    setModalUcs({ usina: null, filtros: FILTROS_REINCLUIR });
    setSearchParams(atual => {
      const proximo = new URLSearchParams(atual);
      proximo.delete('filtro');
      return proximo;
    }, { replace: true });
  }, [linhas.length, setSearchParams]);

  // Ajustes em aberto e quantos o rateio aberto já confirmou.
  const conferencia = useMemo(() => {
    const abertos = ajustes.filter(ajusteAberto);
    const porNumero = new Map<string, LinhaSaldoUc>();
    for (const l of linhas) {
      porNumero.set(l.ucNumero, l);
      if (l.ucNovo) porNumero.set(l.ucNovo, l);
    }
    const confirmados = abertos.filter(a => conferirAjuste(a, porNumero.get(a.uc_numero) || null, mesRateio) === 'confirmado');
    return { abertos, confirmados };
  }, [ajustes, linhas, mesRateio]);

  const mudarStatusAjuste = async (ajuste: RateioAjusteUc, status: RateioAjusteStatus) => {
    setAtualizandoAjusteId(ajuste.id);
    try {
      const atualizado = await atualizarStatusAjuste(ajuste.id, status, profile?.full_name ?? null);
      setAjustes(lista => lista.map(a => (a.id === atualizado.id ? atualizado : a)));
      addToast(`Ajuste marcado como ${STATUS_AJUSTE[status].toLowerCase()}.`, 'success');
    } catch (e: any) {
      addToast('Não foi possível atualizar o ajuste: ' + (e?.message || ''), 'error');
    } finally {
      setAtualizandoAjusteId(null);
    }
  };

  const aplicarConfirmados = async () => {
    if (conferencia.confirmados.length === 0) return;
    setAplicandoLote(true);
    try {
      const atualizados = await Promise.all(
        conferencia.confirmados.map(a => atualizarStatusAjuste(a.id, 'aplicado', profile?.full_name ?? null))
      );
      const porId = new Map(atualizados.map(a => [a.id, a]));
      setAjustes(lista => lista.map(a => porId.get(a.id) || a));
      addToast(`${atualizados.length} ajuste(s) marcado(s) como aplicado(s).`, 'success');
    } catch (e: any) {
      addToast('Não foi possível marcar todos: ' + (e?.message || ''), 'error');
      setAjustes(await carregarAjustes());
    } finally {
      setAplicandoLote(false);
    }
  };

  /** Exporta o rateio inteiro; o modal exporta só o que está filtrado nele. */
  const exportarTudo = () => {
    exportarSaldosUcs(linhas, resumoUsinas, ajustesDaLinha, mesRateio);
    logUsageEvent({ userId: user?.id, modulo: 'gestao', funcionalidade: 'rateio_saldos', acao: 'exportar', origem: 'acao' });
    addToast(`${linhas.length} UC(s) exportada(s).`, 'success');
  };

  const recarregarGeradoras = async () => {
    const geradoras = await carregarGeradoras();
    setUgs(geradoras.ugs);
    setCadastroUgs(geradoras.cadastro);
  };

  // Mesma regra de `destravar_uc` no banco (auth_can_approve_rateio): só adm e gerente.
  const isGestor = profile?.role === 'adm' || profile?.role === 'gerente';

  const destravar = async (linha: LinhaSaldoUc) => {
    if (linha.ucId == null) return;
    try {
      await destravarUc(linha.ucId);
      setUcs(lista => (lista ? lista.map(u => (u.id === linha.ucId ? { ...u, situacao: 'Reativacao' } : u)) : lista));
      addToast(`UC ${linha.ucNumero} destravada: agora a planilha do rateio volta a mudar a situação dela.`, 'success');
    } catch (e: any) {
      addToast('Não foi possível destravar: ' + (e?.message || ''), 'error');
    }
  };

  const linhaDetalhe = detalhe ? linhas.find(l => l.itemId === detalhe.itemId) || detalhe : null;
  const recebendo = useMemo(() => linhas.filter(l => l.recebe).length, [linhas]);

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white font-poppins flex items-center gap-2">
            <BatteryCharging className="text-primary" /> Saldos das UCs
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Saldo de créditos, % do rateio e ajustes de cada UC, a partir do rateio que subiu no Rateio Antigo.
          </p>
        </div>
        <div className="flex items-center flex-wrap gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            Rateio
            <select
              value={rateioId}
              onChange={e => trocarRateio(e.target.value)}
              disabled={carregando || rateios.length === 0}
              className="px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-gray-700 dark:text-slate-200"
            >
              {rateios.map(r => (
                <option key={r.id} value={r.id}>
                  {r.mes_referencia}
                </option>
              ))}
            </select>
          </label>
          <button onClick={recarregarTudo} title="Atualizar" className="p-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800">
            <RefreshCw size={16} className={carregando ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={exportarTudo}
            disabled={carregando || linhas.length === 0}
            title="Exporta todas as UCs do rateio e o quadro das usinas"
            className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 shadow-sm disabled:opacity-50"
          >
            <Download size={16} /> Exportar
          </button>
        </div>
      </div>

      {erro ? (
        <div className="flex items-center gap-2 p-4 rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-sm text-red-700 dark:text-red-400">
          <AlertTriangle size={16} /> {erro}
        </div>
      ) : null}

      {carregando && !dados ? (
        <div className="p-16 flex flex-col items-center gap-3 text-gray-400">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
          <p className="text-sm">Cruzando o rateio com o cadastro e as faturas…</p>
        </div>
      ) : rateios.length === 0 ? (
        <div className="p-12 text-center bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700">
          <Zap className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 dark:text-gray-400">Nenhum rateio encontrado. Suba a planilha do mês em Rateio Antigo.</p>
        </div>
      ) : (
        <>
          <AvisoReinclusaoRateio
            total={reinclusao.total}
            pedidas={reinclusao.pedidas}
            pausadas={reinclusao.pausadas}
            onVer={() => setModalUcs({ usina: null, filtros: FILTROS_REINCLUIR })}
          />

          {conferencia.abertos.length > 0 ? (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm">
              <p className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <ClipboardList size={16} className="text-primary" />
                <span>
                  <strong>{conferencia.abertos.length}</strong> ajuste(s) em aberto.
                  {conferencia.confirmados.length > 0 ? (
                    <> <strong className="text-emerald-700 dark:text-emerald-400">{conferencia.confirmados.length}</strong> já vieram no rateio de {mesRateio}.</>
                  ) : null}
                </span>
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setModalUcs({ usina: null, filtros: { status: 'todos', soAjusteAberto: true } })}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800"
                >
                  Ver essas UCs
                </button>
                {conferencia.confirmados.length > 0 ? (
                  <button
                    onClick={aplicarConfirmados}
                    disabled={aplicandoLote}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 disabled:opacity-50"
                  >
                    {aplicandoLote ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                    Marcar os {conferencia.confirmados.length} como aplicados
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          <section className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4">
              <div>
                <h2 className="flex items-center gap-2 text-sm font-bold text-gray-800 dark:text-white">
                  <Factory size={16} className="text-primary" /> Usinas do rateio de {mesRateio} ({resumoUsinas.length})
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  {recebendo.toLocaleString('pt-BR')} UCs recebendo energia. Clique numa usina para ver as UCs dela.
                </p>
              </div>
              <button
                onClick={() => setModalUcs({ usina: null })}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-sm font-bold hover:opacity-90 shadow-sm"
              >
                <List size={16} /> Ver todas as UCs
              </button>
            </div>
            <div className="px-4 pb-4 overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-700">
                    <th className="py-2 pr-3 font-semibold">Usina</th>
                    <th className="py-2 pr-3 font-semibold text-right">Geração média</th>
                    <th className="py-2 pr-3 font-semibold text-right">UCs</th>
                    <th className="py-2 pr-3 font-semibold">% alocado (vigente)</th>
                    <th className="py-2 pr-3 font-semibold text-right">% sugerido</th>
                    <th className="py-2 font-semibold text-right"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/60">
                  {resumoUsinas.map(r => {
                    const alocado = Math.min(r.somaVigente, 1);
                    const cadastro = cadastroUgs.find(u => u.id === r.ugId) || null;
                    return (
                      <tr
                        key={r.ugNumero}
                        onClick={() => setModalUcs({ usina: r.ugNumero })}
                        className="group cursor-pointer text-gray-700 dark:text-gray-200 hover:bg-gray-50/80 dark:hover:bg-gray-700/40 transition-colors"
                        title="Ver as UCs desta usina"
                      >
                        <td className="py-2 pr-3 pl-1">
                          <span className="flex items-center gap-1 font-semibold group-hover:text-primary">
                            {r.ugNome || 'Usina sem cadastro'}
                            <ChevronRight size={14} className="text-gray-300 group-hover:text-primary" />
                          </span>
                          <span className="block text-[11px] text-gray-400">{r.ugNumero}</span>
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {r.mediaGeracaoKwh != null ? (
                            <>
                              {formatarKwh(r.mediaGeracaoKwh)} kWh
                              <span className="block text-[11px] text-gray-400">{r.mesesGeracao} {r.mesesGeracao === 1 ? 'mês' : 'meses'}</span>
                            </>
                          ) : (
                            <span className="text-amber-600 dark:text-amber-400 text-xs font-semibold">Sem geração registrada</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">{r.ucsRecebendo}</td>
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-28 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                              <div className={`h-full ${r.somaVigente > 1.00005 ? 'bg-red-500' : 'bg-primary'}`} style={{ width: `${alocado * 100}%` }} />
                            </div>
                            <span className="text-xs tabular-nums">{formatarPercentRateio(r.somaVigente)}</span>
                            <span className="text-[11px] text-gray-400 tabular-nums">sobra {formatarPercentRateio(Math.max(0, 1 - r.somaVigente))}</span>
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {formatarPercentRateio(r.somaSugerida)}
                          {r.semSugestao > 0 ? <span className="block text-[11px] text-gray-400">{r.semSugestao} sem sugestão</span> : null}
                        </td>
                        <td className="py-2 text-right" onClick={e => e.stopPropagation()}>
                          {cadastro ? (
                            <button
                              onClick={() => setRegistrarGeracao(cadastro)}
                              className="px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800"
                            >
                              Registrar geração
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
                O % sugerido segue o modelo da planilha: (consumo − mínimo da distribuidora) × 1,15 ÷ geração média da usina. A geração média vem dos meses registrados; para mudar a base, registre a geração do mês.
              </p>
            </div>
            {carregando ? (
              <div className="px-4 py-2 text-[11px] text-gray-400 flex items-center gap-2 border-t border-gray-100 dark:border-gray-700">
                <Loader2 size={12} className="animate-spin" /> Atualizando…
              </div>
            ) : null}
          </section>
        </>
      )}

      {modalUcs ? (
        <UsinaUcsModal
          resumo={resumoUsinas}
          linhas={linhas}
          usinaInicial={modalUcs.usina}
          filtrosIniciais={modalUcs.filtros}
          mesRateio={mesRateio}
          ajustesDaLinha={ajustesDaLinha}
          onAbrirUc={setDetalhe}
          onAjustar={setAjustando}
          onClose={() => setModalUcs(null)}
        />
      ) : null}

      {linhaDetalhe ? (
        <SaldoUcDetalheModal
          linha={linhaDetalhe}
          mesRateio={mesRateio}
          rateios={rateios}
          ugs={ugs}
          ajustes={ajustesDaLinha(linhaDetalhe)}
          atualizandoAjusteId={atualizandoAjusteId}
          onMudarStatusAjuste={mudarStatusAjuste}
          onRegistrarAjuste={() => setAjustando(linhaDetalhe)}
          onDestravar={isGestor && linhaDetalhe.statusCadastro === 'Pausada' && linhaDetalhe.ucId != null ? () => destravar(linhaDetalhe) : undefined}
          onClose={() => setDetalhe(null)}
        />
      ) : null}

      {ajustando ? (
        <RegistrarAjusteModal
          linha={ajustando}
          ugs={ugs}
          mesRateio={mesRateio}
          onClose={() => setAjustando(null)}
          onSalvo={novo => {
            setAjustes(lista => [novo, ...lista]);
            setAjustando(null);
          }}
        />
      ) : null}

      {registrarGeracao ? (
        <RegistrarGeracaoModal
          ug={registrarGeracao}
          meses={gerarUltimosMeses(12)}
          mesInicial={mesRateio || gerarUltimosMeses(1)[0]}
          onClose={() => setRegistrarGeracao(null)}
          onSalvo={() => {
            setRegistrarGeracao(null);
            recarregarGeradoras().catch(e => console.error('Erro ao recarregar usinas', e));
          }}
        />
      ) : null}
    </div>
  );
};

export default SaldosUcsView;
